/**
 * iOS/iPadOS Safari Detection Utilities
 *
 * Provides unified detection for iOS devices and Safari browser.
 * Used to apply iOS-specific workarounds for scroll, touch, and modal behavior.
 */

let _isIOSCached: boolean | null = null;
let _isSafariCached: boolean | null = null;

/**
 * Detects if the current device is iOS (iPhone, iPad, iPod)
 * Includes iPadOS which reports as Mac but has touch capabilities
 */
export function isIOS(): boolean {
  if (_isIOSCached !== null) return _isIOSCached;

  const ua = navigator.userAgent;

  // Standard iOS detection
  if (/iPad|iPhone|iPod/.test(ua)) {
    _isIOSCached = true;
    return true;
  }

  // iPadOS 13+ detection (reports as Mac but has touch)
  // Check for Mac platform with touch support
  const isMac = /Macintosh/.test(ua);
  const hasTouch = navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

  if (isMac && hasTouch) {
    _isIOSCached = true;
    return true;
  }

  _isIOSCached = false;
  return false;
}

/**
 * Detects if the current browser is Safari (not Chrome/Edge/etc on iOS)
 */
export function isSafari(): boolean {
  if (_isSafariCached !== null) return _isSafariCached;

  const ua = navigator.userAgent;
  _isSafariCached = ua.includes('Safari/') && !ua.includes('Chrome/') && !ua.includes('Edg/');
  return _isSafariCached;
}

/**
 * Detects if the current browser is Safari on iOS/iPadOS
 * This is the primary check for applying iOS-specific fixes
 */
export function isIOSSafari(): boolean {
  return isIOS() && isSafari();
}

/**
 * Initialize iOS detection and add appropriate classes to document
 * Should be called early in app initialization
 */
export function initIOSDetection(): void {
  if (isIOS()) {
    document.documentElement.classList.add('ios-device');

    if (isSafari()) {
      document.documentElement.classList.add('ios-safari');
    }

    // Add iPadOS-specific class if applicable
    const ua = navigator.userAgent;
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) {
      document.documentElement.classList.add('ipados-device');
    }
  }
}

/**
 * Get a descriptive string for the current platform (for debugging)
 */
export function getIOSPlatformInfo(): string {
  if (!isIOS()) return 'Not iOS';

  const ua = navigator.userAgent;
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPod/.test(ua)) return 'iPod';
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return 'iPadOS';

  return 'iOS (unknown)';
}
